import { cn } from "@frontend/shared/lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";

const inputVariants = cva({
  variants: {
    variant: {
      default: "",
      error: "border-destructive focus-visible:ring-destructive",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface PhoneInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: "default" | "error";
}

export const formatPhoneInput = (input: string) => {
  // Remove all non-numeric characters
  let cleaned = input.replace(/\D/g, "");

  // If number doesn't start with a country code, assume US/Canada (+1)
  if (!cleaned.startsWith("1")) {
    cleaned = `1${cleaned}`;
  }

  // Ensure we only take the first 11 digits (1 for country code + 10 for phone number)
  cleaned = cleaned.slice(0, 11);

  // Format as +{countrycode}{areacode}{7digits}
  // Only format if we have at least the country code
  if (cleaned.length > 0) {
    return `+${cleaned}`;
  }

  return "";
};

export const formatPhoneDisplay = (input: string) => {
  // Remove non-numeric characters and the country code if present
  const cleaned = input.replace(/\D/g, "").replace(/^1/, "");

  // Format as (XXX)-XXX-XXXX
  let formatted = "";
  if (cleaned.length > 0) formatted += `(${cleaned.slice(0, 3)}`;
  if (cleaned.length > 3) formatted += `) ${cleaned.slice(3, 6)}`;
  if (cleaned.length > 6) formatted += `-${cleaned.slice(6, 10)}`;

  return formatted;
};

export function obfuscatePhone(phone) {
  const last4 = phone.slice(-4);
  return `(***) ***-${last4}`;
}

export const obfuscateEmail = (email: string): string => {
  const [localPart, domain] = email.split("@");
  if (!domain) return email;
  // If local part is exactly 3 characters, show first letter + asterisks
  if (localPart.length === 3) {
    return `${localPart[0]}**@${domain}`;
  }
  const visibleChars = Math.min(3, localPart.length);
  const obfuscated = "*".repeat(Math.max(0, localPart.length - visibleChars));
  return `${obfuscated}${localPart.slice(-visibleChars)}@${domain}`;
};

const PhoneStorefrontInput = React.forwardRef<
  HTMLInputElement,
  PhoneInputProps
>(({ className, variant, form, onChange, ...props }, ref) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneDisplay(e.target.value);

    // Create new synthetic event with formatted value
    const syntheticEvent = {
      ...e,
      target: { ...e.target, value: formatted },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
  };

  return (
    <input
      type="text"
      ref={inputRef}
      className={cn(inputVariants({ className }))}
      onChange={handleChange}
      placeholder="(XXX)-XXX-XXXX"
      maxLength={14}
      {...props}
    />
  );
});

PhoneStorefrontInput.displayName = "PhoneStorefrontInput";

export { PhoneStorefrontInput, type PhoneInputProps };
